import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, MapPin, Calendar, Award, Heart, 
  Plane, Users, Star, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BRANDI_PHOTO = "https://customer-assets-jt897jd0.emergentagent.net/job_tranquil-living-1/artifacts/sjqqjm8y_IMG_5382.webp";

const stats = [
  { icon: Globe, value: '26+', label: 'Countries Visited' },
  { icon: Calendar, value: '2017', label: 'Industry Since' },
  { icon: Users, value: '500+', label: 'Happy Travelers' },
  { icon: Star, value: '5.0', label: 'Client Rating' }
];

const destinations = [
  'Jamaica', 'Mexico', 'Bahamas', 'Dominican Republic', 'Turks & Caicos',
  'Aruba', 'St. Lucia', 'Barbados', 'Greece', 'Italy', 'France', 'Spain',
  'Portugal', 'Croatia', 'United Kingdom', 'Ireland', 'Iceland', 'Norway',
  'Switzerland', 'Netherlands', 'Germany', 'Austria', 'Czech Republic',
  'Thailand', 'Japan', 'Dubai'
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="text-gold font-medium tracking-widest text-sm uppercase">Your Travel Expert</span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mt-4 mb-6">
                Meet Brandi Satterfield
              </h1>
              <p className="text-white/80 text-lg leading-relaxed">
                Luxury travel advisor based in Houston, Texas with a passion for 
                creating unforgettable travel experiences for every client.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-72 h-72 md:w-96 md:h-96 rounded-full overflow-hidden border-4 border-gold shadow-luxury">
                  <img 
                    src={BRANDI_PHOTO} 
                    alt="Brandi Satterfield" 
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-gold rounded-full p-4">
                  <Globe className="w-8 h-8 text-navy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-navy" />
                </div>
                <p className="text-3xl font-serif font-bold text-gold">{stat.value}</p>
                <p className="text-text-muted text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-navy mb-8 text-center">My Story</h2>
          
          <div className="prose prose-lg max-w-none text-text-secondary">
            <p className="mb-6">
              I've been in the travel industry since 2017, and my passion for exploring the world 
              has taken me to more than <span className="text-gold font-semibold">26 countries</span> across 
              multiple continents. This firsthand experience allows me to recommend destinations 
              with confidence and create personalized itineraries that match each client's unique preferences.
            </p>
            
            <p className="mb-6">
              My journey into travel advisory began with my own love of exploration. After planning 
              countless trips for family and friends, I realized I had a knack for creating memorable 
              travel experiences. I decided to turn that passion into a career, and I haven't looked back since.
            </p>
            
            <p className="mb-6">
              I specialize in <span className="text-gold font-semibold">luxury resorts, cruises, group travel, 
              and destination celebrations</span>. Whether you're planning a romantic honeymoon, a milestone 
              birthday trip, a destination wedding, or a family reunion, I bring the same attention to 
              detail and personalized service to every booking.
            </p>
            
            <p className="mb-6">
              Based in <span className="text-gold font-semibold">Houston, Texas</span>, I work with clients 
              across the country to plan their dream vacations. I believe that every traveler deserves 
              exceptional service regardless of their budget, and I'm committed to making your travel 
              dreams a reality.
            </p>
            
            <p>
              My attention to detail, organization skills, and genuine passion for travel help create 
              stress-free vacations from beginning to end. When you work with me, you get more than 
              just a travel advisor — you get a dedicated partner who is invested in making your trip 
              absolutely perfect.
            </p>
          </div>
        </div>
      </section>

      {/* Where I've Been */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-navy mb-4 text-center">Where I've Traveled</h2>
          <p className="text-text-secondary text-center mb-10 max-w-2xl mx-auto">
            My firsthand experience in these destinations helps me provide authentic recommendations 
            and insider tips for your trip.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {destinations.map((destination, index) => (
              <span 
                key={index}
                className="bg-white px-4 py-2 rounded-full text-navy text-sm font-medium shadow-sm hover:shadow-md transition-shadow"
              >
                <MapPin className="w-3 h-3 inline mr-1 text-gold" />
                {destination}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Me */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-navy mb-10 text-center">Why Work With Me</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-navy mb-3">Personalized Service</h3>
              <p className="text-text-secondary">
                Every trip is custom-tailored to your preferences, not a cookie-cutter package.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-navy mb-3">Industry Expertise</h3>
              <p className="text-text-secondary">
                Access to exclusive deals, upgrades, and amenities through supplier partnerships.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-navy mb-3">End-to-End Support</h3>
              <p className="text-text-secondary">
                I'm with you every step of the way, from planning through your return home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Let's Plan Your Next Adventure
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Ready to start planning? Book a consultation and let's make your travel dreams come true.
          </p>
          <Link to="/consultation">
            <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-4 rounded-full text-lg">
              Book Consultation - $25
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
